import React, { useState } from "react";
import {
  Home,
  Users,
  Clock,
  Package,
  UserCheck,
  FileText,
  Briefcase,
  ClipboardPen,
  Bell,
  BarChart2,
  Grid,
  Activity,
  Folder,
  ChevronDown,
  ChevronRight,
  Map,
  MapPin,
  Navigation,
  Layers,
} from "lucide-react";
import { useLocation, Link } from "react-router-dom";

const SidebarMenu = ({
  collapsed,
  searchTerm,
  user,
  styles,
  isMonitoring,
  hasLimitedMenu,
}) => {
  const location = useLocation();
  const [expandedGroups, setExpandedGroups] = useState({
    analytics: true,
    management: true,
    system: true,
    hr: true,
  });
  const [expandedSubmenus, setExpandedSubmenus] = useState({
    projects: false,
  });

  const isAdmin = user?.role === "admin";
  const isAdminProduk = user?.role === "admin-produk";
  const isAdminProyek = user?.role === "admin-proyek";
  const isSupervisor = user?.role === "supervisor";

  // Function to get menu groups based on user role
  const getMenuGroups = () => {
    if (isMonitoring) {
      return {
        analytics: {
          label: "Analytics",
          items: [
            { name: "Overview", icon: BarChart2, path: "/monitoring/overview" },
            {
              name: "Performance",
              icon: Activity,
              path: "/monitoring/performance",
            },
            { name: "Reports", icon: FileText, path: "/monitoring/reports" },
          ],
        },
        system: {
          label: "System",
          items: [
            { name: "Modules", icon: Grid, path: "/monitoring/module" },
            { name: "Alerts", icon: Bell, path: "/monitoring/alerts" },
            { name: "Logs", icon: Folder, path: "/monitoring/logs" },
          ],
        },
      };
    }

    // Admin menu based on role
    if (isSupervisor) {
      return {
        analytics: {
          label: "Analytics",
          items: [{ name: "Dashboard", icon: Home, path: "/admin/dashboard" }],
        },
        management: {
          label: "Management",
          items: [
            {
              name: "Projects",
              icon: Briefcase,
              path: "/admin/projects",
              hasSubmenu: true,
              submenu: [
                {
                  name: "Project List",
                  icon: Briefcase,
                  path: "/admin/projects",
                },
                {
                  name: "Project Map",
                  icon: Map,
                  path: "/admin/projects/map",
                },
                {
                  name: "Excavation Points",
                  icon: MapPin,
                  path: "/admin/excavations",
                },
                /* {
                  name: "CP Mapping",
                  icon: Navigation,
                  path: "/admin/projects/cp-mapping",
                }, */
              ],
            },
          ],
        },
      };
    }

    if (isAdminProduk) {
      return {
        analytics: {
          label: "Analytics",
          items: [{ name: "Dashboard", icon: Home, path: "/admin/dashboard" }],
        },
        management: {
          label: "Management",
          items: [{ name: "Products", icon: Package, path: "/admin/products" }],
        },
      };
    }

    if (isAdminProyek) {
      return {
        analytics: {
          label: "Analytics",
          items: [{ name: "Dashboard", icon: Home, path: "/admin/dashboard" }],
        },
        management: {
          label: "Management",
          items: [
            {
              name: "Projects",
              icon: Briefcase,
              path: "/admin/projects",
              hasSubmenu: true,
              submenu: [
                {
                  name: "Project List",
                  icon: Briefcase,
                  path: "/admin/projects",
                },
                {
                  name: "Project Map",
                  icon: Map,
                  path: "/admin/projects/map",
                },
                {
                  name: "Excavation Points",
                  icon: MapPin,
                  path: "/admin/excavations",
                },
                /* {
                  name: "CP Mapping",
                  icon: Navigation,
                  path: "/admin/projects/cp-mapping",
                }, */
              ],
            },
            { name: "Reports", icon: FileText, path: "/admin/reports" },
          ],
        },
      };
    }

    // Full admin menu
    return {
      analytics: {
        label: "Analytics",
        items: [
          { name: "Dashboard", icon: Home, path: "/admin/dashboard" },
          { name: "Reports", icon: FileText, path: "/admin/reports" },
        ],
      },
      management: {
        label: "Management",
        items: [
          {
            name: "Projects",
            icon: Briefcase,
            path: "/admin/projects",
            hasSubmenu: true,
            submenu: [
              {
                name: "Project List",
                icon: ClipboardPen,
                path: "/admin/projects",
              },
              {
                name: "Map Overview",
                icon: Layers,
                path: "/admin/projects/map-overview",
              },
              {
                name: "Project Map",
                icon: Map,
                path: "/admin/projects/map",
              },
              {
                name: "Excavation Points",
                icon: MapPin,
                path: "/admin/projects/excavation/map",
              },
              /* {
                name: "CP Mapping",
                icon: Navigation,
                path: "/admin/projects/cp-mapping",
              }, */
            ],
          },
          { name: "Products", icon: Package, path: "/admin/products" },
          { name: "Users", icon: Users, path: "/admin/users" },
          { name: "Clients", icon: UserCheck, path: "/admin/client" },
        ],
      },
      /* hr: {
        label: "Human Resources",
        items: [
          { name: "Employees", icon: Users, path: "/admin/employees" },
          { name: "Attendance", icon: Clock, path: "/admin/attendance" },
        ],
      }, */
    };
  };

  const menuGroups = getMenuGroups();

  const toggleGroup = (group) => {
    setExpandedGroups({
      ...expandedGroups,
      [group]: !expandedGroups[group],
    });
  };

  const toggleSubmenu = (itemName) => {
    setExpandedSubmenus({
      ...expandedSubmenus,
      [itemName.toLowerCase()]: !expandedSubmenus[itemName.toLowerCase()],
    });
  };

  const isActive = (path) => location.pathname === path;

  const isSubmenuActive = (submenuItems) => {
    return submenuItems.some((item) => location.pathname === item.path);
  };

  // Filter menu items based on search term
  const filteredMenuItems = () => {
    const allItems = [];

    Object.entries(menuGroups).forEach(([groupKey, group]) => {
      group.items.forEach((item) => {
        // Check main item
        if (item.name.toLowerCase().includes(searchTerm.toLowerCase())) {
          allItems.push({ ...item, group: groupKey });
        }

        // Check submenu items if they exist
        if (item.submenu) {
          item.submenu.forEach((subItem) => {
            if (subItem.name.toLowerCase().includes(searchTerm.toLowerCase())) {
              allItems.push({
                ...subItem,
                group: groupKey,
                isSubmenuItem: true,
                parentName: item.name,
              });
            }
          });
        }
      });
    });

    return allItems;
  };

  const renderMenuItem = (item, isSubitem = false) => {
    const hasSubmenu = item.hasSubmenu && !collapsed;
    const submenuKey = item.name.toLowerCase();
    const isSubmenuExpanded = expandedSubmenus[submenuKey];
    const itemIsActive = hasSubmenu
      ? isSubmenuActive(item.submenu)
      : isActive(item.path);

    return (
      <li key={item.name} className="w-full">
        {hasSubmenu ? (
          <>
            {/* Main menu item with submenu */}
            <div
              className={`rounded-xl cursor-pointer ${
                itemIsActive ? styles.activeItem : styles.hoverItem
              }`}
              onClick={() => toggleSubmenu(item.name)}
            >
              <div
                className={`flex items-center justify-between ${
                  collapsed ? "justify-center" : ""
                } px-3 py-2 transition-colors duration-300`}
                title={item.name}
              >
                <div className="flex items-center gap-3">
                  <item.icon size={collapsed ? 20 : 18} className="shrink-0" />
                  {!collapsed && (
                    <span className={`text-sm ${isSubitem ? "ml-4" : ""}`}>
                      {item.name}
                    </span>
                  )}
                </div>
                {!collapsed && (
                  <div className="transition-transform duration-200">
                    {isSubmenuExpanded ? (
                      <ChevronDown size={16} />
                    ) : (
                      <ChevronRight size={16} />
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Submenu items */}
            {isSubmenuExpanded && !collapsed && (
              <ul className="mt-1 space-y-1 ml-4 border-l-2 border-white/50 pl-2">
                {item.submenu.map((subItem) => (
                  <li
                    key={subItem.path}
                    className={`rounded-xl ${
                      isActive(subItem.path)
                        ? styles.activeItem
                        : styles.hoverItem
                    }`}
                  >
                    <Link
                      to={subItem.path}
                      className="flex items-center gap-3 px-3 py-2 text-sm transition-colors duration-300"
                      title={subItem.name}
                    >
                      <subItem.icon size={16} className="shrink-0" />
                      <span>{subItem.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          /* Regular menu item without submenu */
          <Link
            to={item.path}
            className={`rounded-xl block ${
              itemIsActive ? styles.activeItem : styles.hoverItem
            }`}
          >
            <div
              className={`flex items-center gap-3 ${
                collapsed ? "justify-center" : ""
              } px-3 py-2 transition-colors duration-300`}
              title={item.name}
            >
              <item.icon size={collapsed ? 20 : 18} className="shrink-0" />
              {!collapsed && (
                <span className={`text-sm ${isSubitem ? "ml-4" : ""}`}>
                  {item.isSubmenuItem && item.parentName
                    ? `${item.parentName} - ${item.name}`
                    : item.name}
                </span>
              )}
            </div>
          </Link>
        )}
      </li>
    );
  };

  return (
    <nav className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent">
      {/* Search Results - only show for non-limited menu users */}
      {searchTerm && !collapsed && !hasLimitedMenu && (
        <div className="px-3 mb-4">
          <p className={`text-xs ${styles.groupHeader} mb-2`}>Search Results</p>
          <ul className="space-y-1">
            {filteredMenuItems().map((item) => renderMenuItem(item))}
          </ul>
        </div>
      )}

      {/* Menu Groups - Only show when not searching or for limited menu users */}
      {(!searchTerm || hasLimitedMenu) && (
        <>
          {Object.entries(menuGroups).map(([key, group]) => (
            <div key={key} className="mb-4">
              {/* Group Header - hide for limited menu users when collapsed */}
              {!collapsed && (hasLimitedMenu || !hasLimitedMenu) && (
                <div
                  className={`flex items-center justify-between px-4 py-2 ${
                    hasLimitedMenu ? "" : "cursor-pointer"
                  } ${styles.groupHeader}`}
                  onClick={hasLimitedMenu ? undefined : () => toggleGroup(key)}
                >
                  <span className="text-xs font-medium text-white/80">
                    {group.label}
                  </span>
                  {!hasLimitedMenu && (
                    <>
                      {expandedGroups[key] ? (
                        <ChevronDown size={14} />
                      ) : (
                        <ChevronRight size={14} />
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Group Items - always show for limited menu users */}
              {(collapsed || expandedGroups[key] || hasLimitedMenu) && (
                <ul className="space-y-1 px-3">
                  {group.items.map((item) => renderMenuItem(item))}
                </ul>
              )}
            </div>
          ))}
        </>
      )}
    </nav>
  );
};

export default SidebarMenu;
